import type { CadEntity } from '../model/entity';
import type { CadDocument, Layer } from '../model/document';

/**
 * 연산(Operation) — 편집의 최소 단위 (작업지시서 P2-06)
 *
 * `StoreCAD_기획서_고도화_제안_v2.md` 2.2절의 핵심 자료구조다.
 * 모든 편집은 반드시 연산으로 표현되고, 연산 하나로 다섯 기능이 동시에 굴러간다:
 *
 *   1. Undo/Redo      — invert() 로 역연산
 *   2. 자동 저장       — 명령 완료 시 op 만 append (전체 스냅샷 불필요)
 *   3. 버전 비교       — 두 버전 사이 op 를 읽으면 "무엇이 왜 바뀌었는지" 그대로 나옴
 *   4. 타임라인 되감기 — op 를 순서대로 재생
 *   5. 실시간 협업     — op 를 Realtime 으로 방송
 *
 * kind 값은 DB 의 cad_op_kind enum 과 일치해야 한다 (0002_cad_core.sql).
 */

export type OpKind = 'add' | 'update' | 'delete' | 'layer' | 'meta';

interface OpBase {
  /** 이 연산을 만든 명령. 'LINE' | 'MOVE' | 'OFFSET' … */
  command: string;
}

export interface AddOp extends OpBase {
  kind: 'add';
  entities: CadEntity[];
}

export interface UpdateOp extends OpBase {
  kind: 'update';
  /** 같은 id 끼리 1:1 대응. 순서도 같아야 한다. */
  before: CadEntity[];
  after: CadEntity[];
}

export interface DeleteOp extends OpBase {
  kind: 'delete';
  entities: CadEntity[];
}

export interface LayerOp extends OpBase {
  kind: 'layer';
  before: Layer[];
  after: Layer[];
}

export interface MetaOp extends OpBase {
  kind: 'meta';
  before: Partial<CadDocument>;
  after: Partial<CadDocument>;
}

export type CadOp = AddOp | UpdateOp | DeleteOp | LayerOp | MetaOp;

/** 한 명령이 만들어낸 연산 묶음. Undo 는 이 단위로 되돌린다. */
export interface OpBatch {
  id: string;
  command: string;
  ops: CadOp[];
  at: number;
}

/**
 * 역연산.
 * add ↔ delete, update 는 before/after 교환, layer/meta 도 교환.
 */
export function invert(op: CadOp): CadOp {
  switch (op.kind) {
    case 'add':
      return { kind: 'delete', command: op.command, entities: op.entities };
    case 'delete':
      return { kind: 'add', command: op.command, entities: op.entities };
    case 'update':
      return { kind: 'update', command: op.command, before: op.after, after: op.before };
    case 'layer':
      return { kind: 'layer', command: op.command, before: op.after, after: op.before };
    case 'meta':
      return { kind: 'meta', command: op.command, before: op.after, after: op.before };
  }
}

export function invertBatch(batch: OpBatch): OpBatch {
  return {
    ...batch,
    // 역순으로 되돌려야 중간 상태가 어긋나지 않는다.
    ops: [...batch.ops].reverse().map(invert),
  };
}

/** 이 연산이 건드리는 엔티티 id 들 (감사 로그 · 협업 브로드캐스트용) */
export function touchedIds(op: CadOp): string[] {
  switch (op.kind) {
    case 'add':
    case 'delete':
      return op.entities.map((e) => e.id);
    case 'update':
      return op.after.map((e) => e.id);
    case 'layer':
    case 'meta':
      return [];
  }
}

/** 사람이 읽는 요약 — 명령 히스토리(F2)와 버전 비교에 쓴다. */
export function describeOp(op: CadOp): string {
  switch (op.kind) {
    case 'add':
      return `${op.command}: ${op.entities.length}개 추가`;
    case 'delete':
      return `${op.command}: ${op.entities.length}개 삭제`;
    case 'update':
      return `${op.command}: ${op.after.length}개 변경`;
    case 'layer':
      return `${op.command}: 레이어 변경`;
    case 'meta':
      return `${op.command}: 도면 설정 변경`;
  }
}
